/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import config from "../../config";
import makeRequest from "../../makeRequest";

/* ─── Injected styles ───────────────────────────────────────────────────── */
const STYLE_ID = "dept-details-styles";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes deptFadeUp {
      from { opacity: 0; transform: translateY(30px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes deptShimmer {
      0%   { background-position: -600px 0; }
      100% { background-position: 600px 0; }
    }

    /* ── HTML content text styling ── */
    .dept-html-content h1,
    .dept-html-content h2,
    .dept-html-content h3,
    .dept-html-content h4,
    .dept-html-content h5,
    .dept-html-content h6 {
      font-weight: 700;
      line-height: 1.3;
      margin: 1.4em 0 0.5em;
      color: #0f172a;
    }
    .dept-html-content h1 { font-size: 2rem; }
    .dept-html-content h2 { font-size: 1.6rem; }
    .dept-html-content h3 { font-size: 1.35rem; }
    .dept-html-content h4 { font-size: 1.15rem; }
    .dept-html-content h5 { font-size: 1rem; }
    .dept-html-content h6 { font-size: 0.9rem; }
    .dept-html-content p {
      margin: 0 0 1.1em;
      line-height: 1.8;
      color: #334155;
    }
    .dept-html-content ul,
    .dept-html-content ol {
      margin: 0 0 1.1em 1.5em;
      padding: 0;
      color: #334155;
      line-height: 1.8;
    }
    .dept-html-content li { margin-bottom: 0.35em; }
    .dept-html-content blockquote {
      border-left: 4px solid #1b74bb;
      margin: 1.5em 0;
      padding: 0.75em 1.2em;
      background: #f0f7ff;
      color: #334155;
      font-style: italic;
    }
    .dept-html-content strong { font-weight: 700; }
    .dept-html-content em { font-style: italic; }
    .dept-html-content a { color: #1b74bb; text-decoration: underline; }
    .dept-html-content table {
      width: 100%;
      border-collapse: collapse;
      margin: 1em 0;
    }
    .dept-html-content th,
    .dept-html-content td {
      border: 1px solid #e2e8f0;
      padding: 8px 12px;
      text-align: left;
    }
    .dept-html-content th { background: #f1f5f9; font-weight: 600; }
    .dept-html-content hr {
      border: none;
      border-top: 1px solid #e2e8f0;
      margin: 2em 0;
    }

    /* ── Doctor panel grid ── */
    .dept-doctors-grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 28px;
    }
    @media (max-width: 1199px) {
      .dept-doctors-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    }
    @media (max-width: 767px) {
      .dept-doctors-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
    }
    @media (max-width: 480px) {
      .dept-doctors-grid { grid-template-columns: 1fr; }
    }
    .dept-doctors-grid .it-team-item {
      overflow: hidden;
      border-radius: 10px;
      background: #fff;
      box-shadow: 0 4px 20px rgba(8, 87, 222, 0.10);
      transition: transform 0.3s ease, box-shadow 0.3s ease;
    }
    .dept-doctors-grid .it-team-item:hover {
      transform: translateY(-5px);
      box-shadow: 0 10px 28px rgba(8, 87, 222, 0.16);
    }
    .dept-doctors-grid .it-team-content {
      margin-right: 0 !important;
      margin-top: 0 !important;
      padding: 22px 16px 18px !important;
      box-shadow: none !important;
      border-radius: 0 !important;
    }
  `;
  document.head.appendChild(style);
}

/* ─── Image hooks ───────────────────────────────────────────────────────── */
function useBlobImage(url) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!url) {
      setLoading(false);
      return;
    }
    let objectUrl = null;
    setLoading(true);
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.blob();
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => setSrc(null))
      .finally(() => setLoading(false));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  return { src, loading };
}

/* ─── Initials fallback ─────────────────────────────────────────────────── */
function Initials({ name, size = 80, fontSize = 28 }) {
  const letters = (name || "?")
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  return (
    <div
      style={{
        width: "100%",
        aspectRatio: "3 / 4",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(145deg, #1b74bb, #0d3b6e)",
        fontSize,
        fontWeight: 800,
        color: "#fff",
        letterSpacing: 2,
      }}
    >
      {letters}
    </div>
  );
}

/* ─── Doctor Card ───────────────────────────────────────────────────────── */
function DoctorCard({ doctor, delay }) {
  const baseUrl = config.api.baseUrl;
  const { src, loading: imgLoading } = useBlobImage(doctor.hasImage ? `${baseUrl}/website/doctor/${doctor.id}/image` : null);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} style={{ opacity: visible ? 1 : 0, animation: visible ? `deptFadeUp 0.55s ${delay}s ease both` : "none" }}>
      <div className="it-team-item">
        <div className="it-team-thumb" style={{ position: "relative", background: "#edf5fc" }}>
          {imgLoading ? (
            <div
              style={{
                width: "100%",
                paddingBottom: "112%",
                background: "linear-gradient(90deg, #e2e8f0 0%, #f8fafc 50%, #e2e8f0 100%)",
                backgroundSize: "800px 100%",
                animation: "deptShimmer 1.6s infinite linear",
              }}
            />
          ) : src ? (
            <img
              src={src}
              alt={doctor.name}
              style={{ width: "100%", aspectRatio: "3 / 4", objectFit: "cover", objectPosition: "top center", display: "block" }}
            />
          ) : (
            <Initials name={doctor.name} />
          )}
        </div>

        <div className="it-team-content text-center">
          <div className="it-team-author-box">
            <h4
              className="it-team-title"
              style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", padding: "0 12px" }}
              title={doctor.name}
            >
              {doctor.name}
            </h4>
            <span>{doctor.designation || doctor.speciality || "Consultant"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Shimmer skeleton ──────────────────────────────────────────────────── */
function SkeletonCard() {
  return (
    <div className="it-team-item">
      <div
        style={{
          width: "100%",
          paddingBottom: "112%",
          background: "linear-gradient(90deg, #e2e8f0, #f8fafc, #e2e8f0)",
          backgroundSize: "800px 100%",
          animation: "deptShimmer 1.6s infinite linear",
          borderRadius: 10,
        }}
      />
      <div className="it-team-content text-center">
        <div style={{ height: 18, width: "65%", background: "#e2e8f0", borderRadius: 5, margin: "0 auto 10px" }} />
        <div style={{ height: 13, width: "42%", background: "#f1f5f9", borderRadius: 5, margin: "0 auto" }} />
      </div>
    </div>
  );
}

/* ═══ MAIN ══════════════════════════════════════════════════════════════════ */
const DepartmentDetailsMain = () => {
  const { id } = useParams();
  const baseUrl = config.api.baseUrl;

  const [department, setDepartment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const { src: deptImgSrc } = useBlobImage(department?.hasImage ? `${baseUrl}/website/department/${id}/image` : null);

  useEffect(() => {
    setLoading(true);
    setError(false);
    makeRequest({ route: `/website/department/public/${id}`, method: "get" })
      .then((res) => setDepartment(res?.data || null))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <main>
        <Breadcrumb title="Department" subTitle="Departments" />
        <div style={{ padding: "80px 0", textAlign: "center", color: "#64748b" }}>Loading…</div>
      </main>
    );
  }

  if (error || !department) {
    return (
      <main>
        <Breadcrumb title="Department" subTitle="Departments" />
        <div style={{ padding: "80px 0", textAlign: "center", color: "#ef4444" }}>Department not found.</div>
      </main>
    );
  }

  const doctors = department.doctors || [];

  return (
    <main>
      <Breadcrumb title={department.name} subTitle="Departments" />

      {/* ── Department header ── */}
      <section style={{ paddingTop: 70, paddingBottom: 50, background: "#fff" }}>
        <div className="container">
          <div className="row align-items-center" style={{ gap: "0 0" }}>
            {/* Image */}
            {deptImgSrc && (
              <div className="col-xl-5 col-lg-5 mb-40">
                <div
                  style={{
                    borderRadius: 14,
                    overflow: "hidden",
                    boxShadow: "0 8px 32px rgba(8, 87, 222, 0.13)",
                  }}
                >
                  <img src={deptImgSrc} alt={department.name} style={{ width: "100%", height: "auto", display: "block" }} />
                </div>
              </div>
            )}

            {/* Title + description */}
            <div className={deptImgSrc ? "col-xl-7 col-lg-7 mb-40" : "col-12 mb-40"}>
              <div style={{ paddingLeft: deptImgSrc ? 20 : 0 }}>
                <span
                  style={{
                    display: "inline-block",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 3,
                    textTransform: "uppercase",
                    color: "#1b74bb",
                    marginBottom: 14,
                  }}
                >
                  Department
                </span>
                <h1
                  style={{
                    fontSize: "clamp(26px, 3.5vw, 42px)",
                    fontWeight: 800,
                    color: "#0f172a",
                    lineHeight: 1.2,
                    marginBottom: 20,
                  }}
                >
                  {department.name}
                </h1>
                {/* Accent underline */}
                <div style={{ display: "flex", gap: 5, marginBottom: 22 }}>
                  <div style={{ width: 48, height: 4, borderRadius: 2, background: "#1b74bb" }} />
                  <div style={{ width: 14, height: 4, borderRadius: 2, background: "#1b74bb80" }} />
                  <div style={{ width: 6, height: 4, borderRadius: 2, background: "#1b74bb30" }} />
                </div>
                {department.description && (
                  <p style={{ fontSize: 16, color: "#475569", lineHeight: 1.8, margin: 0 }}>{department.description}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HTML Details content ── */}
      {department.details && (
        <section style={{ paddingTop: 10, paddingBottom: 60, background: "#fff" }}>
          <div className="container">
            <div className="dept-html-content" dangerouslySetInnerHTML={{ __html: department.details }} />
          </div>
        </section>
      )}

      {/* ── Doctors panel ── */}
      {doctors.length > 0 && (
        <section style={{ paddingTop: 70, paddingBottom: 80, background: "#f8fafc" }}>
          <div className="container">
            {/* Section heading */}
            <div style={{ textAlign: "center", marginBottom: 52 }}>
              <span
                style={{
                  display: "inline-block",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 3,
                  textTransform: "uppercase",
                  color: "#1b74bb",
                  marginBottom: 12,
                }}
              >
                Medical Panel
              </span>
              <h2
                style={{
                  fontSize: "clamp(22px, 3vw, 36px)",
                  fontWeight: 800,
                  color: "#0f172a",
                  margin: "0 0 16px",
                }}
              >
                Our Doctors
              </h2>
              <div style={{ display: "flex", justifyContent: "center", gap: 5 }}>
                <div style={{ width: 44, height: 4, borderRadius: 2, background: "#1b74bb" }} />
                <div style={{ width: 12, height: 4, borderRadius: 2, background: "#1b74bb80" }} />
                <div style={{ width: 5, height: 4, borderRadius: 2, background: "#1b74bb30" }} />
              </div>
            </div>

            <div className="dept-doctors-grid">
              {doctors.map((doctor, idx) => (
                <DoctorCard key={doctor.id} doctor={doctor} delay={Math.min(idx % 4, 3) * 0.08} />
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default DepartmentDetailsMain;
